import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { History as HistoryIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Dialog,
  HighlightMatch,
  InputAdornment,
  TextField,
} from '@/shared/ui';
import { usePermission } from '@/shared/hooks/usePermission';
import { prefetchRoute } from '@/shared/routing/routePrefetch';
import { useAuth } from '@/shared/context/AuthContext';
import { useTimezone } from '@/shared/context/TimezoneContext';
import {
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  GLOBAL_SEARCH_RESULT_LIMIT,
  ensureBuiltinSearchRegistered,
  globalSearchClient,
  globalSearchSectionIcon,
  type GlobalSearchHit,
  type GlobalSearchSectionMeta,
  type GlobalSearchSectionResult,
} from '@/shared/search';
import { PatientSpotlightSummaryPanel } from '@/modules/patient/search/PatientSpotlightSummaryPanel';
import { usePatientSpotlightSummary } from '@/modules/patient/search/usePatientSpotlightSummary';
import {
  SPOTLIGHT_STATIC_ACTIONS,
  buildNavSpotlightActions,
  filterSpotlightActions,
  getContextualActionsForHit,
  mergeSpotlightActions,
  partitionSpotlightActions,
  pushRecent,
  readRecent,
  spotlightActionIcon,
  stepSpotlightIndex,
  useSpotlightHost,
  type SpotlightActionDef,
  type SpotlightContextualActionDef,
  type SpotlightRecentItem,
} from '@/shared/layout/spotlight';
import { formatChordLabel } from '@/shared/layout/shortcuts/formatChord';
import { Kbd } from '@/shared/layout/shortcuts/Kbd';
import { useCreateDialogHost } from '@/shared/layout/create-host';

ensureBuiltinSearchRegistered();

const DEBOUNCE_MS = 300;

type FlatItem =
  | { kind: 'action'; id: string; action: SpotlightActionDef }
  | { kind: 'hit'; id: string; hit: GlobalSearchHit }
  | {
      kind: 'ctx';
      id: string;
      hit: GlobalSearchHit;
      ctx: SpotlightContextualActionDef;
      path: string;
      state?: object;
    }
  | { kind: 'recent'; id: string; recent: SpotlightRecentItem };

const shortcutLabel = (): string => formatChordLabel({ kind: 'mod', key: 'k' });

const FooterHint: React.FC<{ keys: React.ReactNode; label: string }> = ({ keys, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>{keys}</Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      px: 2,
      py: 0.75,
      display: 'block',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    }}
  >
    {children}
  </Typography>
);

/**
 * AppBar Spotlight (⌘K): entity search + curated actions/commands + nav index (no LLM).
 */
const GlobalSearch: React.FC = () => {
  const { t } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const { formatInOrgTimezone } = useTimezone();
  const {
    registerOpenHandler,
    setSpotlightOpen,
    setCanOpenSpotlight,
  } = useSpotlightHost();
  const { tryOpenCreate } = useCreateDialogHost();

  const enabledSections = useMemo(
    () => globalSearchClient.getEnabledSections(hasPermission),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasPermission identity changes with auth
    [hasPermission, user?.role],
  );
  const sectionMetaById = useMemo(() => {
    const map = new Map<string, GlobalSearchSectionMeta>();
    for (const meta of enabledSections) {
      map.set(meta.id, meta);
    }
    return map;
  }, [enabledSections]);
  const canEntitySearch = enabledSections.length > 0;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sections, setSections] = useState<GlobalSearchSectionResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<SpotlightRecentItem[]>([]);
  const requestSeq = useRef(0);
  const dialogInputRef = useRef<HTMLInputElement>(null);

  const todayYmd = useMemo(
    () => formatInOrgTimezone(new Date(), 'yyyy-MM-dd'),
    [formatInOrgTimezone],
  );

  const ctxHelpers = useMemo(() => ({ todayYmd }), [todayYmd]);

  const catalogActions = useMemo(
    () =>
      mergeSpotlightActions(SPOTLIGHT_STATIC_ACTIONS, buildNavSpotlightActions()),
    [],
  );

  const actionAllowed = (action: SpotlightActionDef): boolean => {
    if (user?.role === 'admin') return true;
    if (action.permissionsAny && action.permissionsAny.length > 0) {
      return action.permissionsAny.some((p) => hasPermission(p));
    }
    if (!action.permission) return true;
    return hasPermission(action.permission);
  };

  const hasAnyAction = useMemo(
    () => catalogActions.some(actionAllowed),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasPermission/user drive actionAllowed
    [catalogActions, hasPermission, user?.role],
  );

  const resolveActionLabel = (action: SpotlightActionDef): string => {
    if (action.navLabelKey) return tNav(action.navLabelKey);
    if (action.labelKey) return t(action.labelKey);
    return action.id;
  };

  const matchedActions = useMemo(
    () =>
      filterSpotlightActions(catalogActions, {
        query,
        getLabel: resolveActionLabel,
        hasPermission: (permission) => {
          if (user?.role === 'admin') return true;
          return hasPermission(permission);
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolveActionLabel via t/tNav
    [catalogActions, query, t, tNav, hasPermission, user?.role],
  );

  const { primary: primaryActions, go: goActions } = useMemo(
    () => partitionSpotlightActions(matchedActions),
    [matchedActions],
  );

  const canOpen = canEntitySearch || hasAnyAction;

  useEffect(() => {
    setCanOpenSpotlight(canOpen);
  }, [canOpen, setCanOpenSpotlight]);

  useEffect(() => {
    setSpotlightOpen(open);
  }, [open, setSpotlightOpen]);

  const openDialog = (opts?: { query?: string }) => {
    if (!canOpen) return;
    if (opts?.query !== undefined) {
      setQuery(opts.query);
      setDebouncedQuery(opts.query.trim());
    }
    setRecentItems(readRecent());
    setOpen(true);
  };

  useEffect(() => {
    return registerOpenHandler((opts) => {
      if (!canOpen) return;
      if (opts?.query !== undefined) {
        setQuery(opts.query);
        setDebouncedQuery(opts.query.trim());
      }
      setRecentItems(readRecent());
      setOpen(true);
    });
  }, [registerOpenHandler, canOpen]);

  const closeDialog = () => {
    setOpen(false);
    setQuery('');
    setDebouncedQuery('');
    setSections([]);
    setActiveIndex(0);
    requestSeq.current += 1;
  };

  const flatHits = useMemo(
    () => sections.flatMap((section) => section.hits),
    [sections],
  );

  const flatItems = useMemo((): FlatItem[] => {
    const items: FlatItem[] = [];

    for (const recent of recentItems) {
      items.push({ kind: 'recent', id: `recent:${recent.path}`, recent });
    }

    for (const action of primaryActions) {
      items.push({ kind: 'action', id: `action:${action.id}`, action });
    }
    for (const action of goActions) {
      items.push({ kind: 'action', id: `action:${action.id}`, action });
    }

    for (const hit of flatHits) {
      items.push({ kind: 'hit', id: `hit:${hit.sectionId}:${hit.id}`, hit });
      const ctxActions = getContextualActionsForHit(hit, hasPermission);
      for (const ctx of ctxActions) {
        items.push({
          kind: 'ctx',
          id: `ctx:${ctx.id}:${hit.sectionId}:${hit.id}`,
          hit,
          ctx,
          path: ctx.buildPath(hit, ctxHelpers),
          state: ctx.buildState?.(hit, ctxHelpers),
        });
      }
    }

    return items;
  }, [primaryActions, goActions, flatHits, hasPermission, recentItems, ctxHelpers]);

  /** Recent rows occupy [0, leftCount); main column is the rest. */
  const leftCount = recentItems.length;
  const rightCount = Math.max(0, flatItems.length - leftCount);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    setActiveIndex(rightCount > 0 ? leftCount : 0);
  }, [query, sections, leftCount, rightCount]);

  useEffect(() => {
    if (!open || flatItems.length === 0) return;
    const el = document.querySelector<HTMLElement>(
      `[data-spotlight-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open, flatItems.length]);

  useEffect(() => {
    if (!open || !canEntitySearch) return undefined;

    if (debouncedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
      setSections([]);
      return undefined;
    }

    const seq = ++requestSeq.current;
    const controller = new AbortController();

    setSections(
      enabledSections.map((meta) => ({
        id: meta.id,
        loading: true,
        error: false,
        hits: [],
      })),
    );

    void globalSearchClient
      .search({
        query: debouncedQuery,
        limit: GLOBAL_SEARCH_RESULT_LIMIT,
        signal: controller.signal,
        hasPermission,
      })
      .then((settled) => {
        if (seq !== requestSeq.current) return;
        setSections(settled);
      });

    return () => {
      controller.abort();
    };
  }, [open, debouncedQuery, canEntitySearch, enabledSections, hasPermission]);

  const activeItem = flatItems[activeIndex] ?? null;
  const activePatientHit =
    activeItem?.kind === 'hit' && activeItem.hit.sectionId === 'patients'
      ? activeItem.hit
      : activeItem?.kind === 'ctx' && activeItem.hit.sectionId === 'patients'
        ? activeItem.hit
        : null;
  const showPatientNextAppointment =
    user?.role === 'admin' || hasPermission('appointments:read');
  const showPatientOutstanding =
    user?.role === 'admin' || hasPermission('treatment-plan:read');
  /** Reserved for the whole session (permission-based) so selecting a patient never resizes the dialog. */
  const showPatientSummary =
    enabledSections.some((meta) => meta.id === 'patients') &&
    (showPatientNextAppointment || showPatientOutstanding);

  const patientSummary = usePatientSpotlightSummary({
    patientId: showPatientSummary ? activePatientHit?.id ?? null : null,
    includeNextAppointment: showPatientNextAppointment,
    includeOutstandingBalance: showPatientOutstanding,
  });

  if (!canOpen) return null;

  const sectionTitle = (id: string): string => {
    const meta = sectionMetaById.get(id);
    return meta ? t(meta.titleKey as 'globalSearchPatients') : id;
  };

  const sectionIcon = (id: string) => {
    const meta = sectionMetaById.get(id);
    return globalSearchSectionIcon(meta?.iconId ?? id);
  };

  const handleSelectPath = (
    path: string,
    recentMeta?: { id: string; label: string; kind: SpotlightRecentItem['kind'] },
    state?: object,
  ) => {
    if (recentMeta) {
      setRecentItems(
        pushRecent({
          id: recentMeta.id,
          path,
          label: recentMeta.label,
          kind: recentMeta.kind,
          ...(state ? { state } : {}),
        }),
      );
    }
    closeDialog();

    if (
      recentMeta &&
      tryOpenCreate(recentMeta.id, { state, path })
    ) {
      return;
    }

    navigate(path, state ? { state } : undefined);
  };

  const handleActivate = (item: FlatItem) => {
    if (item.kind === 'action') {
      handleSelectPath(
        item.action.path,
        {
          id: item.action.id,
          label: resolveActionLabel(item.action),
          kind: 'action',
        },
        item.action.state,
      );
      return;
    }
    if (item.kind === 'ctx') {
      handleSelectPath(
        item.path,
        {
          id: `${item.ctx.id}:${item.hit.id}`,
          label: `${t(item.ctx.labelKey)} · ${item.hit.primary}`,
          kind: 'ctx',
        },
        item.state,
      );
      return;
    }
    if (item.kind === 'recent') {
      handleSelectPath(
        item.recent.path,
        {
          id: item.recent.id,
          label: item.recent.label,
          kind: item.recent.kind,
        },
        item.recent.state,
      );
      return;
    }
    handleSelectPath(item.hit.path, {
      id: item.hit.id,
      label: item.hit.primary,
      kind: 'hit',
    });
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (flatItems.length === 0) return;
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const includeNested = event.altKey;
      setActiveIndex((i) =>
        stepSpotlightIndex({
          current: i,
          delta,
          leftCount,
          rightCount,
          isNestedRow: (index) => flatItems[index]?.kind === 'ctx',
          includeNested,
        }),
      );
      return;
    }
    if (event.key === 'ArrowLeft') {
      if (leftCount === 0) return;
      event.preventDefault();
      setActiveIndex((i) => {
        if (i < leftCount) return i;
        const local = i - leftCount;
        return Math.min(local, leftCount - 1);
      });
      return;
    }
    if (event.key === 'ArrowRight') {
      if (rightCount === 0) return;
      event.preventDefault();
      setActiveIndex((i) => {
        if (i >= leftCount) return i;
        return leftCount + Math.min(i, rightCount - 1);
      });
      return;
    }
    if (event.key === 'Enter') {
      const item = flatItems[activeIndex];
      if (!item) return;
      event.preventDefault();
      handleActivate(item);
    }
  };

  const searchingEntities =
    debouncedQuery.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH && canEntitySearch;
  const anyLoading = sections.some((s) => s.loading);
  const anyHits = flatHits.length > 0;
  const searchedEntities = searchingEntities && sections.length > 0 && !anyLoading;
  const showPrimary = primaryActions.length > 0;
  const showGo = goActions.length > 0;
  const showActions = showPrimary || showGo;
  const showEmptyNoMatch =
    query.trim().length > 0 &&
    !showActions &&
    !anyHits &&
    !anyLoading &&
    (!searchingEntities || searchedEntities);
  const showRightEmpty =
    query.trim().length === 0 && !showActions && !anyHits && !anyLoading;

  const flatIndexForHit = (hit: GlobalSearchHit): number =>
    flatItems.findIndex(
      (item) => item.kind === 'hit' && item.hit.sectionId === hit.sectionId && item.hit.id === hit.id,
    );

  const flatIndexForAction = (actionId: string): number =>
    flatItems.findIndex((item) => item.kind === 'action' && item.action.id === actionId);

  const flatIndexForCtx = (ctxId: string, hit: GlobalSearchHit): number =>
    flatItems.findIndex(
      (item) =>
        item.kind === 'ctx' &&
        item.ctx.id === ctxId &&
        item.hit.sectionId === hit.sectionId &&
        item.hit.id === hit.id,
    );

  const flatIndexForRecent = (path: string): number =>
    flatItems.findIndex((item) => item.kind === 'recent' && item.recent.path === path);

  const renderActionRows = (actions: SpotlightActionDef[]) => (
    <List dense disablePadding>
      {actions.map((action) => {
        const index = flatIndexForAction(action.id);
        const label = resolveActionLabel(action);
        return (
          <ListItemButton
            key={action.id}
            data-spotlight-index={index}
            selected={index === activeIndex}
            onMouseEnter={() => {
              setActiveIndex(index);
              prefetchRoute(action.path);
            }}
            onClick={() =>
              handleSelectPath(action.path, {
                id: action.id,
                label,
                kind: 'action',
              })
            }
            sx={{ px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {spotlightActionIcon(action.icon)}
            </ListItemIcon>
            <ListItemText
              primary={<HighlightMatch text={label} query={query.trim()} />}
              primaryTypographyProps={{ noWrap: true, component: 'div' }}
            />
          </ListItemButton>
        );
      })}
    </List>
  );

  return (
    <>
      <TextField
        size="small"
        value=""
        placeholder={t('globalSearchPlaceholder')}
        aria-label={t('globalSearch')}
        inputProps={{ readOnly: true }}
        onClick={() => openDialog()}
        onFocus={(e) => {
          e.target.blur();
          openDialog();
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ display: { xs: 'none', md: 'flex' }, mr: 0.5 }}>
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: 0.2,
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {shortcutLabel()}
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          width: '100%',
          maxWidth: 420,
          cursor: 'pointer',
          '& .MuiOutlinedInput-root': {
            cursor: 'pointer',
            height: 36,
            borderRadius: 2,
            bgcolor: 'grey.100',
            transition: 'background-color 0.15s ease',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              bgcolor: 'grey.200',
            },
            '&.Mui-focused': {
              bgcolor: 'grey.100',
            },
          },
          '& .MuiInputBase-input': {
            cursor: 'pointer',
            py: 0.75,
            fontSize: 14,
            '&::placeholder': {
              color: 'text.secondary',
              opacity: 0.85,
            },
          },
        }}
      />

      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth={false}
        onKeyDown={handleDialogKeyDown}
        slotProps={{
          paper: {
            sx: {
              width: showPatientSummary ? '64vw' : '58vw',
              maxWidth: showPatientSummary ? '64vw' : '58vw',
              height: '58vh',
              maxHeight: '58vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              m: 2,
            },
          },
        }}
        TransitionProps={{
          onEntered: () => dialogInputRef.current?.focus(),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            flexShrink: 0,
          }}
        >
          <SearchIcon color="action" />
          <InputBase
            inputRef={dialogInputRef}
            autoFocus
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('globalSearchPlaceholder')}
            aria-label={t('globalSearch')}
            sx={{
              fontSize: '1.05rem',
              '& input::placeholder': {
                opacity: 0.55,
              },
            }}
          />
          {anyLoading ? (
            <CircularProgress size={18} sx={{ flexShrink: 0 }} />
          ) : (
            <Kbd>esc</Kbd>
          )}
        </Box>
        <Divider />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '32%' },
              minWidth: { sm: 200 },
              maxWidth: { sm: '40%' },
              maxHeight: { xs: '32%', sm: 'none' },
              flexShrink: 0,
              borderRight: {
                sm: '1px solid rgba(0, 0, 0, 0.06)',
              },
              borderBottom: {
                xs: '1px solid rgba(0, 0, 0, 0.06)',
                sm: 'none',
              },
              overflow: 'auto',
              py: 1,
              bgcolor: { sm: 'grey.50' },
            }}
          >
            <SectionHeading>{t('globalSearchRecent')}</SectionHeading>
            {recentItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, py: 1.5 }}
              >
                {t('globalSearchRecentEmpty')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {recentItems.map((recent) => {
                  const index = flatIndexForRecent(recent.path);
                  return (
                    <ListItemButton
                      key={recent.path}
                      data-spotlight-index={index}
                      selected={index === activeIndex}
                      onMouseEnter={() => {
                        setActiveIndex(index);
                        prefetchRoute(recent.path);
                      }}
                      onClick={() =>
                        handleSelectPath(recent.path, {
                          id: recent.id,
                          label: recent.label,
                          kind: recent.kind,
                        })
                      }
                      sx={{ px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <HistoryIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={recent.label}
                        primaryTypographyProps={{
                          sx: {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {showRightEmpty && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('globalSearchEmpty')}
                </Typography>
              </Box>
            )}

            {showEmptyNoMatch && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('globalSearchNoMatch')}
                </Typography>
              </Box>
            )}

            {searchingEntities && anyLoading && !anyHits && !showActions && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {(showActions || anyHits) && (
              <Box sx={{ py: 1 }}>
                {showPrimary && (
                  <Box sx={{ mb: 0.5 }}>
                    <SectionHeading>{t('globalSearchActions')}</SectionHeading>
                    {renderActionRows(primaryActions)}
                  </Box>
                )}

                {showGo && (
                  <Box sx={{ mb: 0.5 }}>
                    {showPrimary && <Divider sx={{ my: 1 }} />}
                    <SectionHeading>{t('globalSearchGo')}</SectionHeading>
                    {renderActionRows(goActions)}
                  </Box>
                )}

                {anyHits && (
                  <>
                    {showActions && <Divider sx={{ my: 1 }} />}
                    {sections
                      .filter((section) => section.error || section.hits.length > 0)
                      .map((section, sectionIdx) => (
                        <Box key={section.id} sx={{ mb: 0.5 }}>
                          <SectionHeading>
                            {sectionIdx === 0 && showActions
                              ? `${t('globalSearchResults')} · ${sectionTitle(section.id)}`
                              : sectionTitle(section.id)}
                          </SectionHeading>
                          {section.error ? (
                            <Typography variant="body2" color="error" sx={{ px: 2, py: 1 }}>
                              {t('globalSearchSectionError')}
                            </Typography>
                          ) : (
                            <List dense disablePadding>
                              {section.hits.map((hit) => {
                                const index = flatIndexForHit(hit);
                                const ctxActions = getContextualActionsForHit(
                                  hit,
                                  hasPermission,
                                );
                                return (
                                  <React.Fragment key={`${section.id}-${hit.id}`}>
                                    <ListItemButton
                                      data-spotlight-index={index}
                                      selected={index === activeIndex}
                                      onMouseEnter={() => {
                                        setActiveIndex(index);
                                        prefetchRoute(hit.path);
                                      }}
                                      onClick={() =>
                                        handleSelectPath(hit.path, {
                                          id: hit.id,
                                          label: hit.primary,
                                          kind: 'hit',
                                        })
                                      }
                                      sx={{ px: 2 }}
                                    >
                                      <ListItemIcon sx={{ minWidth: 36 }}>
                                        {sectionIcon(section.id)}
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={
                                          <HighlightMatch
                                            text={hit.primary}
                                            query={debouncedQuery}
                                          />
                                        }
                                        secondary={
                                          hit.secondary ? (
                                            <HighlightMatch
                                              text={hit.secondary}
                                              query={debouncedQuery}
                                            />
                                          ) : undefined
                                        }
                                        primaryTypographyProps={{
                                          noWrap: true,
                                          component: 'div',
                                        }}
                                        secondaryTypographyProps={{
                                          noWrap: true,
                                          component: 'div',
                                        }}
                                      />
                                    </ListItemButton>
                                    {ctxActions.map((ctx) => {
                                      const ctxIndex = flatIndexForCtx(ctx.id, hit);
                                      const ctxPath = ctx.buildPath(hit, ctxHelpers);
                                      const ctxState = ctx.buildState?.(hit, ctxHelpers);
                                      const ctxLabel = t(ctx.labelKey);
                                      // Recent rows show out of context, so keep the subject there.
                                      const ctxRecentLabel = `${ctxLabel} · ${hit.primary}`;
                                      return (
                                        <ListItemButton
                                          key={`${ctx.id}-${hit.id}`}
                                          data-spotlight-index={ctxIndex}
                                          selected={ctxIndex === activeIndex}
                                          onMouseEnter={() => {
                                            setActiveIndex(ctxIndex);
                                            prefetchRoute(ctxPath);
                                          }}
                                          onClick={() =>
                                            handleSelectPath(
                                              ctxPath,
                                              {
                                                id: `${ctx.id}:${hit.id}`,
                                                label: ctxRecentLabel,
                                                kind: 'ctx',
                                              },
                                              ctxState,
                                            )
                                          }
                                          sx={{ pl: 6, pr: 2 }}
                                        >
                                          <ListItemIcon sx={{ minWidth: 36 }}>
                                            {spotlightActionIcon(
                                              ctx.id.includes('task')
                                                ? 'task'
                                                : 'appointment',
                                            )}
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={
                                              <HighlightMatch
                                                text={ctxLabel}
                                                query={query.trim()}
                                              />
                                            }
                                            primaryTypographyProps={{
                                              noWrap: true,
                                              component: 'div',
                                              variant: 'body2',
                                            }}
                                          />
                                        </ListItemButton>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </List>
                          )}
                        </Box>
                      ))}
                  </>
                )}
              </Box>
            )}
          </Box>

          {showPatientSummary && (
            <PatientSpotlightSummaryPanel
              patientName={activePatientHit?.primary ?? null}
              loading={patientSummary.loading}
              error={patientSummary.error}
              summary={patientSummary.summary}
              showNextAppointment={showPatientNextAppointment}
              showOutstandingBalance={showPatientOutstanding}
            />
          )}
        </Box>

        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1.5, sm: 2.5 },
            px: 2,
            py: 1.25,
            flexShrink: 0,
            bgcolor: 'grey.50',
          }}
        >
          <FooterHint
            keys={
              <>
                <Kbd>↓</Kbd>
                <Kbd>↑</Kbd>
              </>
            }
            label={t('globalSearchFooterNavigate')}
          />
          <FooterHint
            keys={
              <>
                <Kbd>alt</Kbd>
                <Kbd>↓</Kbd>
              </>
            }
            label={t('globalSearchFooterStepAll')}
          />
          <FooterHint
            keys={
              <>
                <Kbd>←</Kbd>
                <Kbd>→</Kbd>
              </>
            }
            label={t('globalSearchFooterColumns')}
          />
          <FooterHint keys={<Kbd>↵</Kbd>} label={t('globalSearchFooterSelect')} />
          <FooterHint keys={<Kbd>esc</Kbd>} label={t('globalSearchFooterClose')} />
          <FooterHint
            keys={<Kbd>{shortcutLabel()}</Kbd>}
            label={t('globalSearchFooterOpen')}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default GlobalSearch;
